import React from 'react';
import { Card, CardHeader, CardContent, CardActions, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: '16px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
        transform: 'translateY(-2px)',
    },
}));

const GoldBorderCard = styled(StyledCard)(({ theme }) => ({
    border: '2px solid #D4AF37',
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
    borderBottom: '1px solid #f0f0f0',
    paddingBottom: '16px',
    marginBottom: '16px',
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '0',
        left: '16px',
        width: '80px',
        height: '4px',
        background: 'linear-gradient(to right, #D4AF37, #FFB800)',
        borderRadius: '2px',
    },
}));

interface MemerCardProps {
    children: React.ReactNode;
    goldBorder?: boolean;
    hover?: boolean;
    onClick?: () => void;
}

export const MemerCard: React.FC<MemerCardProps> = ({
    children,
    goldBorder = false,
    hover = true,
    onClick
}) => {
    const CardComponent = goldBorder ? GoldBorderCard : StyledCard;

    return (
        <CardComponent
            onClick={onClick}
            sx={{
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': hover ? {} : { transform: 'none', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }
            }}
        >
            {children}
        </CardComponent>
    );
};

export const MemerCardHeader: React.FC<{ title: string; subtitle?: string }> = ({
    title,
    subtitle
}) => (
    <StyledCardHeader
        title={
            <Typography variant="h4" component="h2">
                {title}
            </Typography>
        }
        subheader={subtitle && (
            <Typography variant="body2" color="text.secondary">
                {subtitle}
            </Typography>
        )}
    />
);

export const MemerCardContent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <CardContent>
        {children}
    </CardContent>
);

export const MemerCardActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <CardActions sx={{ padding: '16px', justifyContent: 'space-between' }}>
        {children}
    </CardActions>
);

// Specialized card variants
export const MemerStatsCard: React.FC<{
    title: string;
    value: string;
    subtitle?: string;
    icon?: React.ReactNode;
    trend?: 'up' | 'down' | 'neutral';
}> = ({ title, value, subtitle, icon, trend }) => (
    <MemerCard>
        <MemerCardContent>
            <Box textAlign="center">
                {icon && (
                    <Box display="flex" justifyContent="center" mb={2} color="#D4AF37" fontSize="2rem">
                        {icon}
                    </Box>
                )}
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    {title}
                </Typography>
                <Typography variant="h3" color="text.primary" gutterBottom>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                )}
                {trend && (
                    <Typography
                        variant="caption"
                        sx={{
                            mt: 1,
                            display: 'block',
                            fontWeight: 600,
                            color: trend === 'up' ? '#2E7D32' : trend === 'down' ? '#ef4444' : '#6B6B6B'
                        }}
                    >
                        {trend === 'up' && '↗ Trending up'}
                        {trend === 'down' && '↘ Trending down'}
                        {trend === 'neutral' && '→ Stable'}
                    </Typography>
                )}
            </Box>
        </MemerCardContent>
    </MemerCard>
);
